import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { listingAPI } from '../../services/api/listingAPI'
import { getTextareaClasses } from '../../styles/inputStyles'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'

const logger = createLogger('ListingRequestModal')

/**
 * ListingRequestModal - lets a requester express interest in a listing.
 * The poster receives the message and decides whether to accept or decline.
 * A private channel is only created after the poster accepts.
 *
 * @param {Object}   listing   - listing data (id, title, display_name)
 * @param {Function} onClose   - close the modal
 * @param {Function} onSent    - called after request is submitted
 * @idempotent @atomic
 */
const ListingRequestModal = memo(function ListingRequestModal({ listing, onClose, onSent }) {
  const { t } = useTranslation()
  const { execute, loading } = useAsyncOperation()
  const [message, setMessage] = useState('')

  const MAX_CHARS = 300
  const remaining = MAX_CHARS - message.length

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.()
    const trimmed = message.trim()
    if (trimmed.length < 5) {
      toast.error(t('listings.request.messageTooShort', 'Please write at least 5 characters'))
      return
    }
    await execute(() => listingAPI.requestListing(listing.id, { message: trimmed }), {
      onSuccess: () => {
        toast.success(t('listings.request.sent', 'Request sent — waiting for the poster to respond'))
        onSent?.()
        onClose()
      },
      onError: (err) => {
        logger.error('Failed to send listing request', err)
        const msg = err?.response?.data?.message
        if (err?.response?.status === 403) {
          toast.error(t('listings.request.popRequired', 'Spot something on the map first to send requests'))
        } else if (msg) {
          toast.error(msg)
        } else {
          toast.error(t('listings.request.failed', 'Failed to send request'))
        }
      },
    })
  }, [message, listing.id, execute, t, onSent, onClose])

  return (
    <div
      className="fixed inset-0 z-[2100] flex items-end sm:items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              {t('listings.request.title', "I'm interested")}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
              {listing.title} &middot; {t('listings.postedBy', 'Posted by')} {listing.display_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            onTouchEnd={(e) => { e.preventDefault(); onClose() }}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2">
          <p className="text-xs text-gray-500 leading-snug">
            {t('listings.request.hint',
              'The poster will review your message and decide whether to connect with you. Your exact location is never shared automatically.'
            )}
          </p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
            placeholder={t('listings.request.placeholder',
              'Hi! I can pick up today after 3pm. Would that work?'
            )}
            rows={3}
            autoFocus
            className={getTextareaClasses(false)}
          />
          <p className={`text-xs text-right ${remaining < 30 ? 'text-accent' : 'text-gray-400'}`}>
            {remaining} {t('common.charsLeft', 'left')}
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            onTouchEnd={(e) => { e.preventDefault(); onClose() }}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={loading || message.trim().length < 5}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-colors"
            style={{ background: 'rgb(var(--color-primary))' }}
          >
            {loading
              ? t('common.sending', 'Sending...')
              : t('listings.request.sendButton', 'Send request')
            }
          </button>
        </div>
      </form>
    </div>
  )
})

export default ListingRequestModal
